import React, { useState, useEffect } from 'react';
import { FiX, FiSave, FiPlus, FiMinus, FiEdit2 } from 'react-icons/fi';
import { inventarioService } from '../../services';
import './MovimientoStockModal.scss';

const MovimientoStockModal = ({ isOpen, onClose, producto, onSuccess }) => {
  const [formData, setFormData] = useState({
    operation: 'add',
    quantity: '',
    reason: '',
    notes: '',
    unit_cost: producto?.unit_cost || ''
  });
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [selectedReason, setSelectedReason] = useState('');

  // Obtener razones predefinidas según el tipo de operación
  const reasons = inventarioService.getStockReasons();
  const availableReasons = reasons[formData.operation] || [];

  useEffect(() => {
    if (isOpen && producto) {
      setFormData({
        operation: 'add',
        quantity: '',
        reason: '',
        notes: '',
        unit_cost: producto.unit_cost || ''
      });
      setErrors({});
      setSelectedReason('');
    }
  }, [isOpen, producto]);

  const handleOperationChange = (operation) => {
    setFormData({
      ...formData,
      operation,
      reason: '' // Resetear razón al cambiar operación
    });
    setSelectedReason('');
  };

  const handleReasonChange = (value) => {
    setSelectedReason(value);
    setFormData({
      ...formData,
      reason: value === 'custom' ? '' : value
    });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.quantity || parseFloat(formData.quantity) <= 0) {
      newErrors.quantity = 'La cantidad debe ser mayor a 0';
    }

    if (!formData.reason || formData.reason.trim() === '') {
      newErrors.reason = 'Debe indicar la razón del movimiento';
    }

    // Validar que no se reste más de lo disponible
    if (formData.operation === 'subtract') {
      const cantidadActual = producto?.stock || producto?.current_stock || 0;
      if (parseFloat(formData.quantity) > cantidadActual) {
        newErrors.quantity = `No puede restar más de ${cantidadActual} unidades disponibles`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSaving(true);

    try {
      const stockData = {
        operation: formData.operation,
        quantity: parseFloat(formData.quantity),
        reason: formData.reason,
        notes: formData.notes || null,
        unit_cost: formData.unit_cost ? parseFloat(formData.unit_cost) : null
      };

      const result = await inventarioService.updateStock(producto.product_id || producto.id, stockData);

      if (result.success) {
        if (onSuccess) {
          onSuccess(result.data);
        }
        onClose();
      } else {
        setErrors({ submit: result.message || 'Error al actualizar el stock' });
      }
    } catch (error) {
      console.error('Error guardando movimiento:', error);
      setErrors({ submit: error.message || 'Error al guardar el movimiento' });
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen || !producto) return null;

  const stockActual = producto.stock || producto.current_stock || 0;
  const stockMinimo = producto.stock_min || producto.min_stock || 0;

  // Calcular nuevo stock según operación
  const calcularNuevoStock = () => {
    if (!formData.quantity) return stockActual;

    const cantidad = parseFloat(formData.quantity);
    switch (formData.operation) {
      case 'add':
        return stockActual + cantidad;
      case 'subtract':
        return stockActual - cantidad;
      case 'set':
        return cantidad;
      default:
        return stockActual;
    }
  };

  const nuevoStock = calcularNuevoStock();

  return (
    <div className="movimiento-stock-modal-overlay" onClick={onClose}>
      <div className="movimiento-stock-modal-content movimiento-stock-modal-content--medium" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="movimiento-stock-modal-header">
          <div>
            <h2 className="movimiento-stock-modal-title">Movimiento de Inventario</h2>
            <p className="movimiento-stock-modal-subtitle">{producto.product_name || producto.name}</p>
          </div>
          <button className="movimiento-stock-modal-close" onClick={onClose} type="button">
            <FiX />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit}>
          <div className="movimiento-stock-modal-body">
            {/* Información del Producto */}
            <div className="info-section">
              <div className="info-grid">
                <div className="info-item">
                  <label className="info-label">Stock Actual</label>
                  <div className="info-value info-value--highlight">{stockActual}</div>
                </div>
                <div className="info-item">
                  <label className="info-label">Stock Mínimo</label>
                  <div className="info-value">{stockMinimo}</div>
                </div>
              </div>
            </div>

            {errors.submit && (
              <div className="alert alert--danger">
                {errors.submit}
              </div>
            )}

            {/* Tipo de Operación */}
            <div className="form-group">
              <label className="form-label">Tipo de Movimiento *</label>
              <div className="operation-buttons">
                <button
                  type="button"
                  className={`operation-btn ${formData.operation === 'add' ? 'operation-btn--active operation-btn--success' : ''}`}
                  onClick={() => handleOperationChange('add')}
                >
                  <FiPlus /> Entrada
                </button>
                <button
                  type="button"
                  className={`operation-btn ${formData.operation === 'subtract' ? 'operation-btn--active operation-btn--danger' : ''}`}
                  onClick={() => handleOperationChange('subtract')}
                >
                  <FiMinus /> Salida
                </button>
                <button
                  type="button"
                  className={`operation-btn ${formData.operation === 'set' ? 'operation-btn--active operation-btn--info' : ''}`}
                  onClick={() => handleOperationChange('set')}
                >
                  <FiEdit2 /> Ajustar
                </button>
              </div>
            </div>

            {/* Cantidad */}
            <div className="form-group">
              <label className="form-label" htmlFor="quantity">
                {formData.operation === 'set' ? 'Nueva Cantidad *' : 'Cantidad *'}
              </label>
              <input
                id="quantity"
                type="number"
                className={`form-input ${errors.quantity ? 'form-input--error' : ''}`}
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                placeholder="Ingrese la cantidad"
                min="0"
                step="0.01"
                required
              />
              {errors.quantity && (
                <span className="form-error">{errors.quantity}</span>
              )}
            </div>

            {/* Nuevo Stock (Preview) */}
            {formData.quantity && (
              <div className="stock-preview">
                <div className="stock-preview__label">Nuevo Stock:</div>
                <div className={`stock-preview__value ${nuevoStock < stockMinimo ? 'stock-preview__value--warning' : 'stock-preview__value--success'}`}>
                  {nuevoStock}
                  {nuevoStock < stockMinimo && (
                    <span className="stock-preview__badge">Bajo mínimo</span>
                  )}
                </div>
              </div>
            )}

            {/* Razón del Movimiento */}
            <div className="form-group">
              <label className="form-label" htmlFor="reason-select">Razón del Movimiento *</label>
              <select
                id="reason-select"
                className="form-input"
                value={selectedReason}
                onChange={(e) => handleReasonChange(e.target.value)}
                required
              >
                <option value="">Seleccione una razón...</option>
                {availableReasons.map((reason, index) => (
                  <option key={index} value={reason}>
                    {reason}
                  </option>
                ))}
                <option value="custom">Otra razón (personalizada)</option>
              </select>
            </div>

            {/* Razón Personalizada */}
            {selectedReason === 'custom' && (
              <div className="form-group">
                <label className="form-label" htmlFor="custom-reason">Razón Personalizada *</label>
                <input
                  id="custom-reason"
                  type="text"
                  className={`form-input ${errors.reason ? 'form-input--error' : ''}`}
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  placeholder="Describa la razón del movimiento"
                  required
                />
                {errors.reason && (
                  <span className="form-error">{errors.reason}</span>
                )}
              </div>
            )}

            {/* Costo Unitario (opcional para entradas) */}
            {formData.operation === 'add' && (
              <div className="form-group">
                <label className="form-label" htmlFor="unit-cost">Costo Unitario (opcional)</label>
                <input
                  id="unit-cost"
                  type="number"
                  className="form-input"
                  value={formData.unit_cost}
                  onChange={(e) => setFormData({ ...formData, unit_cost: e.target.value })}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                />
                <small className="form-hint">
                  Actualiza el costo unitario del producto en esta entrada
                </small>
              </div>
            )}

            {/* Notas Adicionales */}
            <div className="form-group">
              <label className="form-label" htmlFor="notes">Notas Adicionales</label>
              <textarea
                id="notes"
                className="form-input form-textarea"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Información adicional sobre este movimiento..."
                rows="3"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="movimiento-stock-modal-footer">
            <button
              type="button"
              className="btn btn--ghost"
              onClick={onClose}
              disabled={isSaving}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn--primary"
              disabled={isSaving}
            >
              <FiSave /> {isSaving ? 'Guardando...' : 'Guardar Movimiento'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MovimientoStockModal;
